import React from 'react';
import Modal from '@shared/ui/Modal';
import Button from '@shared/ui/Button';
import CFRecordSections, { CFSection } from './CFRecordSections';
import CommitteeLink from '../committee/CommitteeLink';
import type { Expenditure, Loan } from '../../types/campaignFinance';
import { fmtMoney, fmtDate } from '../../utils/campaignFinanceUtils';

interface CFTransactionModalProps {
  entity: 'expenditures' | 'loans';
  record: Expenditure | Loan;
  open: boolean;
  onClose: () => void;
}

const payeeName = (e: Expenditure): string =>
  e.payee_name || [e.first_name, e.middle_initial, e.last_name, e.suffix].filter(Boolean).join(' ').trim() || 'Unknown';

const loanEvent = (l: Loan): string => (l.type === 'O' ? 'Origination' : l.type === 'P' ? 'Repayment' : (l.type || '—'));

function expenditureSections(e: Expenditure): CFSection[] {
  return [
    {
      title: 'Payee',
      fields: [
        { label: 'Name', value: payeeName(e) },
        { label: 'City', value: [e.city, e.state].filter(Boolean).join(', ') },
        { label: 'ZIP', value: e.zip_code },
        { label: 'Employer', value: e.employer },
        { label: 'Occupation', value: e.occupation },
      ],
    },
    {
      title: 'Expenditure',
      fields: [
        { label: 'Amount', value: fmtMoney(Number(e.expenditure_amount) || 0) },
        { label: 'Date', value: e.expenditure_date ? fmtDate(e.expenditure_date) : undefined },
        { label: 'Expenditure Type', value: e.expenditure_type },
        { label: 'Disbursement', value: e.disbursement_type },
        { label: 'Payment Type', value: e.payment_type },
        { label: 'Filed Date', value: e.filed_date ? fmtDate(e.filed_date) : undefined },
        { label: 'Explanation', value: e.explanation },
      ],
    },
    {
      title: 'Committee',
      fields: [
        { label: 'Committee', value: e.committee_name ? <CommitteeLink coId={e.co_id} name={e.committee_name} className="text-primary-700 dark:text-primary-dark-400" /> : null },
        { label: 'Committee Type', value: e.committee_type },
        { label: 'Candidate', value: e.candidate_name },
      ],
    },
    {
      title: 'Record',
      fields: [
        { label: 'CO ID', value: e.co_id },
        { label: 'Record ID', value: e.record_id },
        { label: 'Jurisdiction', value: e.jurisdiction },
        { label: 'Source', value: [e.source, e.level].filter(Boolean).join(' · ') },
        { label: 'Amended', value: e.amended ? 'Yes' : 'No' },
        { label: 'Amendment Type', value: e.amendment },
        { label: 'Amended Record ID', value: e.amended_record_id },
        { label: 'Orphan Amendment', value: e.is_orphan_amendment ? 'Yes' : undefined },
      ],
    },
  ];
}

function loanSections(l: Loan): CFSection[] {
  return [
    {
      title: 'Lender',
      fields: [
        { label: 'Name', value: l.name },
        { label: 'Lender Type', value: l.loan_source_type },
        { label: 'City', value: [l.city, l.state].filter(Boolean).join(', ') },
        { label: 'ZIP', value: l.zip_code },
      ],
    },
    {
      title: 'Loan',
      fields: [
        { label: 'Event', value: loanEvent(l) },
        { label: 'Loan Amount', value: l.loan_amount != null ? fmtMoney(Number(l.loan_amount)) : undefined },
        { label: 'Loan Date', value: l.loan_date ? fmtDate(l.loan_date) : undefined },
        { label: 'Payment Amount', value: l.payment_amount != null ? fmtMoney(Number(l.payment_amount)) : undefined },
        { label: 'Payment Date', value: l.payment_date ? fmtDate(l.payment_date) : undefined },
        { label: 'Outstanding Balance', value: l.loan_balance != null ? fmtMoney(Number(l.loan_balance)) : undefined },
        { label: 'Interest Rate', value: l.interest_rate != null ? `${Number(l.interest_rate)}%` : undefined },
        { label: 'Interest Payment', value: l.interest_payment != null ? fmtMoney(Number(l.interest_payment)) : undefined },
        { label: 'Description', value: l.description },
      ],
    },
    {
      title: 'Committee',
      fields: [
        { label: 'Committee', value: l.committee_name ? <CommitteeLink coId={l.co_id} name={l.committee_name} className="text-primary-700 dark:text-primary-dark-400" /> : null },
        { label: 'Committee Type', value: l.committee_type },
        { label: 'Candidate', value: l.candidate_name },
      ],
    },
    {
      title: 'Record',
      fields: [
        { label: 'CO ID', value: l.co_id },
        { label: 'Record ID', value: l.record_id },
        { label: 'Jurisdiction', value: l.jurisdiction },
        { label: 'Source', value: [l.source, l.level].filter(Boolean).join(' · ') },
        { label: 'Amended', value: l.amended ? 'Yes' : 'No' },
        { label: 'Amendment Type', value: l.amendment },
        { label: 'Amended Record ID', value: l.amended_record_id },
      ],
    },
  ];
}

/** Read-only transaction-detail modal for an expenditure or loan record. */
const CFTransactionModal: React.FC<CFTransactionModalProps> = ({ entity, record, open, onClose }) => {

  const sections = entity === 'expenditures'
    ? expenditureSections(record as Expenditure)
    : loanSections(record as Loan);

  return (
    <Modal
      isOpen={open}
      title={entity === 'expenditures' ? 'Expenditure record' : 'Loan record'}
      description={`CO-${record.co_id} · Record ${record.record_id}`}
      onClose={onClose}
      size="xl"
      footer={
        <Button variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <CFRecordSections sections={sections} />
    </Modal>
  );
};

export default CFTransactionModal;
